import config from '@/config';
import {
  ApiBase,
  ListOptions,
  ExerciseGameType,
  InternationalizedField
} from '@/api/common';
import ApiList from './list';
import ListeningContent from './listeningContent';
import ConversationContent from './conversationContent';
import SpeakingContent from './speakingContent';
import SpeakingAltContent from './speakingAltContent';
import axios, { AxiosResponse } from 'axios';
import store from '@/store';
import ReferenceAlignment from '@/api/referenceAlignment';
import Lesson from '@/api/lesson';
import Video from '@/api/video';
import { isEmpty, isNil, min } from 'lodash';

const NO_UPLOAD_FIELDS = [
  'listening_content',
  'image_link',
  'video_object',
  'conversation_content',
  'speaking_content',
  'speaking_alt_content',
  'speaking_alt_content_objects',
  'created',
  'updated'
];

export default class Exercise extends ApiBase<Exercise> {
  public static readonly SLUG: string = 'exercise';
  private static readonly URL_BASE: string = config.API_ROOT + '/exercise/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public listening_content?: ListeningContent = undefined;
  public conversation_content?: ConversationContent = undefined;
  public speaking_content?: SpeakingContent = undefined;
  public speaking_alt_content?: SpeakingAltContent = undefined;
  public speaking_alt_content_objects: SpeakingAltContent[] = [];
  public game_type?: string = undefined;
  public order?: number = undefined;
  public parent_lesson?: number = undefined;
  public updated?: Date = undefined;
  public intelligibility_ready?: boolean = undefined;
  public game_sub_type?: string = undefined;
  public scoring_mode?: string = undefined;
  public isDirty: boolean = true;
  public bg_image?: number = undefined;
  public bg_image_link?: string = undefined;
  public bg_image_path?: number = undefined;
  public insight_info: InternationalizedField<string> = new InternationalizedField<string>();

  public free_speak: boolean = true; // default is alway have the sentence
  public intro_text: InternationalizedField<string> = new InternationalizedField<string>();
  public free_speech_time_limit: number = 30;

  // For conversation free
  public image_url?: number = undefined;
  public image_url_link?: string = undefined;
  public video_url?: number[] = undefined;
  public video_object: Video[] = [];
  // --end: For conversation free

  // currently unused
  public spreadsheet?: string = undefined;

  public constructor(game_type?: string, id?: string) {
    super({
      urlBase: Exercise.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });

    if (game_type) {
      this.game_type = game_type;

      if (
        game_type === ExerciseGameType.CONVERSATION ||
        game_type === ExerciseGameType.CONVERSATION_FREE ||
        game_type === ExerciseGameType.VIDEO_CONVERSATION ||
        game_type === ExerciseGameType.PICK_YOUR_ANSWER
      ) {
        this.conversation_content = new ConversationContent();
      }

      if (
        game_type === ExerciseGameType.LISTEN_AUDIO2TEXT ||
        game_type === ExerciseGameType.LISTEN_TEXT2AUDIO
      ) {
        this.listening_content = new ListeningContent();
      } else {
        this.speaking_content = new SpeakingContent();
        this.speaking_alt_content = new SpeakingAltContent();
      }
    }

    if (id) {
      this.id = id;
    }
  }

  public assign(response: any): Exercise {
    super.assign(response);

    if (response) {
      if ('listening_content' in response) {
        this.listening_content = new ListeningContent().assign(
          response.listening_content
        );
      }
      if ('conversation_content' in response) {
        this.conversation_content = new ConversationContent().assign(
          response.conversation_content
        );
      }
      if ('speaking_content' in response) {
        this.speaking_content = new SpeakingContent().assign(
          response.speaking_content
        );
      }

      if ('speaking_alt_content' in response) {
        response.speaking_alt_content.forEach((speaking_alt_content: any) => {
          speaking_alt_content.state = 'updated';
          this.speaking_alt_content_objects.push(
            new SpeakingAltContent().assign(speaking_alt_content)
          );
        });
      }

      if ('insight_info' in response) {
        this.insight_info = new InternationalizedField<string>(
          response.insight_info
        );
      }

      if ('intro_text' in response) {
        this.intro_text = new InternationalizedField<string>(
          response.intro_text
        );
      }

      if ('video_url' in response) {
        this.video_url = undefined;
        if(!isEmpty(response.video_url)) {
          response.video_url.forEach((video: any) => {
            console.log('video exercise---', video);
            this.video_object.push(new Video().assign(video));
          });
        }
      }
    }

    return this;
  }

  public static async get(id: number): Promise<Exercise> {
    return this.doGet({
      urlBase: Exercise.URL_BASE,
      id,
      create: (response) => new Exercise().assign(response)
    });
  }

  public static async getJson(id: number): Promise<any> {
    return this.doGet({
      urlBase: Exercise.URL_BASE,
      id,
      create: (response) => response
    });
  }

  public static async list(): Promise<ApiList<Exercise>> {
    const options: ListOptions<Exercise> = {
      urlBase: Exercise.URL_BASE,
      create: (response) => new Exercise().assign(response)
    };
    return this.doList(options);
  }

  public static async getExercisesOfLesson(
    id: number,
    minimal = true
  ): Promise<ApiList<Exercise>> {
    const options: ListOptions<Exercise> = {
      urlBase: Exercise.URL_BASE,
      create: (response) => new Exercise().assign(response),
      all: true,
      parameters: minimal
        ? {
            parent_lesson: id,
            minimal: true
          }
        : {
            parent_lesson: id,
            full: true
          }
    };
    return this.doList(options);
  }

  public getName(): string {
    if (this.game_type && this.order) {
      return (
        this.order + ' - ' + ExerciseGameType.typeToPresentation(this.game_type)
      );
    } else if (this.id === 'END') {
      return 'END';
    } else if (this.game_type) {
      return ExerciseGameType.typeToPresentation(this.game_type);
    } else {
      return '';
    }
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }

  public async checkAudioConversation() {
    let ts = Date.now().toString();
    let bodyFormData = new FormData();

    bodyFormData.append('sentence', this.conversation_content.sentence);
    bodyFormData.append('ts', ts.substring(0, ts.length - 3));

    const transcript_arpabet = this.speaking_content.transcript_arpabet;
    if (!isEmpty(transcript_arpabet)) {
      bodyFormData.append('transcript', JSON.stringify(transcript_arpabet));
    } else {
      bodyFormData.append('transcript', '[]');
    }

    if (!isNil(this.conversation_content.audio_path)) {
      const _response: AxiosResponse<any> = await axios({
        method: 'GET',
        url:
          config.API_ROOT +
          '/media-audio/' +
          this.conversation_content.audio_path,
        data: bodyFormData,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
      bodyFormData.append('packet_file', _response.data.public_url);
    }

    const response: AxiosResponse<any> = await axios({
      method: 'POST',
      url: config.API_ROOT + '/reference-score/',
      data: bodyFormData,
      headers: {
        'X-Session': store.state.sessionToken
      }
    });
    if (response && response.data.reference_score.success !== false) {
      return response.data.reference_score;
    }
  }

  public async checkAudioSpeaking() {
    let ts = Date.now().toString();
    let bodyFormData = new FormData();

    bodyFormData.append('sentence', this.speaking_content.sentence);
    bodyFormData.append('ts', ts.substring(0, ts.length - 3));

    const transcript_arpabet = this.speaking_content.transcript_arpabet;
    if (!isEmpty(transcript_arpabet)) {
      bodyFormData.append('transcript', JSON.stringify(transcript_arpabet));
    } else {
      bodyFormData.append('transcript', '[]');
    }

    if (!isNil(this.speaking_content.audio_path)) {
      const _response: AxiosResponse<any> = await axios({
        method: 'GET',
        url:
          config.API_ROOT + '/media-audio/' + this.speaking_content.audio_path,
        data: bodyFormData,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
      bodyFormData.append('packet_file', _response.data.public_url);
    }

    const response: AxiosResponse<any> = await axios({
      method: 'POST',
      url: config.API_ROOT + '/reference-score/',
      data: bodyFormData,
      headers: {
        'X-Session': store.state.sessionToken
      }
    });
    if (response && response.data.reference_score.success !== false) {
      return response.data.reference_score;
    }
  }

  public async updateReferenceScore() {
    this.speaking_content.reference_alignments = [];
    let ts = Date.now().toString();
    let bodyFormData = new FormData();

    bodyFormData.append('sentence', this.speaking_content.sentence);
    bodyFormData.append(
      'transcript',
      JSON.stringify(this.speaking_content.transcript_arpabet)
    );
    bodyFormData.append('sampling_rate', '16000');
    bodyFormData.append('return_phoneme_hints', '0');
    bodyFormData.append('user_nativeness_level', '100');
    bodyFormData.append('ts', ts.substring(0, ts.length - 3));
    bodyFormData.append(
      'exercise_type',
      this.game_type.toLowerCase() + '_exercise'
    );

    if (!isNil(this.speaking_content.audio_path)) {
      const _response: AxiosResponse<any> = await axios({
        method: 'GET',
        url:
          config.API_ROOT + '/media-audio/' + this.speaking_content.audio_path,
        data: bodyFormData,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
      bodyFormData.append('packet_file', _response.data.public_url);
    }

    const response: AxiosResponse<any> = await axios({
      method: 'POST',
      url: config.API_ROOT + '/reference-score/',
      data: bodyFormData,
      headers: {
        'X-Session': store.state.sessionToken
      }
    });
    try {
      if (response && response.data.reference_score.success !== false) {
        this.speaking_content.reference_score =
          response.data.reference_score.utterance[0].nativeness_score;
        response.data.reference_score.utterance[0].words.forEach(
          (word: any) => {
            this.speaking_content.reference_alignments.push(
              new ReferenceAlignment().assign(word)
            );
          }
        );
        this.speaking_content.reference_alignments.forEach((word: any) => {
          word.start_time = this.sec2time(word.start_time);
          word.end_time = this.sec2time(word.end_time);
        });
        await this.updateLessonScore();
      }
    } catch (e) {
      return e;
    }
  }

  public sec2time(timeInSeconds) {
    let pad = function (num, size) {
        return ('000' + num).slice(size * -1);
      },
      time = parseFloat(timeInSeconds).toFixed(3),
      hours = Math.floor(Number(time) / 60 / 60),
      minutes = Math.floor(Number(time) / 60) % 60,
      seconds = Math.floor(Number(time) - minutes * 60),
      milliseconds = time.slice(-3);

    return (
      pad(hours, 2) +
      ':' +
      pad(minutes, 2) +
      ':' +
      pad(seconds, 2) +
      ':' +
      pad(milliseconds, 3)
    );
  }

  private async updateLessonScore() {
    let lesson: Lesson = await Lesson.get(this.parent_lesson);
    await lesson.getReferenceScore();
  }
}
